/* eslint-disable jsx-a11y/click-events-have-key-events */
import classNames from 'classnames';
import React, { forwardRef, LegacyRef, MouseEventHandler } from 'react';

import { Icon as UI5Icon, IconDomRef, IconPropTypes } from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons-business-suite/dist/AllIcons.js';
import '@ui5/webcomponents-icons-tnt/dist/AllIcons.js';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

import styles from './Icon.module.css';
import { FpaIcon, fpaIconSet, SbaIcon, sbaIconSet } from '../../icons';

export interface IconProps extends Omit<IconPropTypes, 'onClick'> {
  /*
   * Sets aria-label of icon.
   */
  'aria-label'?: string;

  // We want to have intellisense for the sba icons
  // eslint-disable-next-line @typescript-eslint/ban-types
  name: SbaIcon | FpaIcon | (string & {});

  onClick?: MouseEventHandler<HTMLSpanElement> | IconPropTypes['onClick'];
}

const isFpaIcon = (name: string): name is FpaIcon => name.startsWith('fpa/');
const isSbaIcon = (name: string): name is SbaIcon => name.startsWith('sba_');

export type IconRole = 'img' | 'presentation' | 'button';
const getRole = (mode: IconPropTypes['mode']): IconRole => {
  switch (mode) {
    case 'Interactive':
      return 'button';
    case 'Decorative':
      return 'presentation';
    default:
      return 'img';
  }
};

const isMouseEventHandler = (
  interactive: boolean,
  handler: unknown,
): handler is MouseEventHandler<HTMLSpanElement> =>
  interactive && typeof handler === 'function';

export const Icon = forwardRef<
  LegacyRef<IconDomRef> | LegacyRef<HTMLSpanElement>,
  IconProps
>((props, ref) => {
  const {
    'aria-label': ariaLabel,
    accessibleName,
    className,
    design,
    mode,
    name,
    showTooltip,
    style,
    onClick,
    ...restProps
  } = props;

  const role = getRole(mode);
  const interactive = mode === 'Interactive';
  const isDecorative = mode === 'Decorative';
  const displayTitle = !ariaLabel ? `${name} icon` : undefined;

  const iconStyles = classNames(
    styles.icon,
    design && design !== 'Default' && styles[design],
    {
      [styles.interactive]: interactive,
      [className as string]: !!className,
    },
  );

  if (isSbaIcon(name)) {
    if (!sbaIconSet[name]) {
      return null;
    }

    return (
      <span
        className={iconStyles}
        onClick={isMouseEventHandler(interactive, onClick) ? onClick : undefined}
        ref={ref as LegacyRef<HTMLSpanElement>}
        role={role}
        style={style}
        title={displayTitle}
        {...restProps}
      >
        <svg
          aria-hidden={isDecorative}
          aria-label={!isDecorative && ariaLabel ? ariaLabel : undefined}
          fill="none"
          focusable={interactive}
          role={role}
          tabIndex={interactive ? 0 : undefined}
          viewBox={sbaIconSet[name].viewBox}
          xmlns="http://www.w3.org/2000/svg"
        >
          {ariaLabel && <title>{ariaLabel}</title>}
          <g>
            {sbaIconSet[name].path.map((d, i) => (
              <path
                clipRule="evenodd"
                d={d}
                fill="currentColor"
                fillRule="evenodd"
                key={i}
              />
            ))}
          </g>
        </svg>
      </span>
    );
  }

  if (isFpaIcon(name)) {
    if (!fpaIconSet[name]) {
      return null;
    }

    return (
      <span
        className={iconStyles}
        onClick={isMouseEventHandler(interactive, onClick) ? onClick : undefined}
        ref={ref as LegacyRef<HTMLSpanElement>}
        role={role}
        style={style}
        title={displayTitle}
        {...restProps}
      >
        <svg
          aria-hidden={isDecorative}
          aria-label={!isDecorative && ariaLabel ? ariaLabel : undefined}
          fill="none"
          focusable={interactive}
          role={role}
          tabIndex={interactive ? 0 : undefined}
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          {ariaLabel && <title>{ariaLabel}</title>}
          <g>
            {fpaIconSet[name].path.map((d, i) => (
              <path
                clipRule="evenodd"
                d={d}
                fill="currentColor"
                fillRule="evenodd"
                key={i}
              />
            ))}
          </g>
        </svg>
      </span>
    );
  }

  return (
    <UI5Icon
      accessibleName={ariaLabel || accessibleName}
      className={className}
      design={design}
      mode={mode}
      name={name}
      ref={ref as LegacyRef<IconDomRef>}
      showTooltip={showTooltip}
      style={style}
      title={displayTitle}
      {...restProps}
    />
  );
});
